'use client';

import { useState, useEffect, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import { FileText, Download, ExternalLink, Lock, PlayCircle, Clock, ArrowUpCircle } from 'lucide-react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { parseVideoUrl, recordingPath } from '@/lib/video/embed';
import { useTimeBoundaries } from '@/lib/hooks/useTimeBoundaries';
import { isWithinUpgradeWindow } from '@/lib/utils/upgrade-window';
import { formatShortDateTime } from '@/lib/utils/date';
import { fetchMyMaterials, type MyMaterial, type MaterialLockReason } from '@/lib/materials/myMaterials';
import { withSignedFiles, asDownloadUrl } from '@/lib/materials/materialFiles';

interface MaterialsDisplayProps {
  eventId: string;
  eventEndDate: string | null;
  onUpgradeTicket?: () => void;
}

/**
 * The event page's Materials section. Which materials the participant may
 * open is decided on the server (get_my_materials): an unpaid reservation, a
 * ticket without materials, or a window that has not opened (or has closed)
 * comes back locked and without links, shown title-only in calm grey with the
 * reason. When an opening time passes while the page is open, the list is
 * refetched so the material unlocks without a reload.
 */
export default function MaterialsDisplay({
  eventId,
  eventEndDate,
  onUpgradeTicket,
}: MaterialsDisplayProps) {
  const { t, language } = useLanguage();
  const uk = language === 'uk';
  const supabase = useMemo(() => createClient(), []);
  const [materials, setMaterials] = useState<MyMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasUpgradeTarget, setHasUpgradeTarget] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // Uploaded files live in a private bucket: each stored link is swapped
        // for a short-lived signed one, created with the participant's session.
        const rows = await withSignedFiles(supabase, await fetchMyMaterials(supabase, { contentId: eventId }));
        let upgradeTarget = false;
        if (rows.some((r) => r.lock_reason === 'ticket')) {
          // Offer the upgrade only if some ticket on the event includes materials.
          const { data: tickets } = await supabase
            .from('tickets')
            .select('id')
            .eq('content_id', eventId)
            .eq('allow_materials', true)
            .limit(1);
          upgradeTarget = (tickets?.length ?? 0) > 0;
        }
        if (cancelled) return;
        setMaterials(rows);
        setHasUpgradeTarget(upgradeTarget);
      } catch (error) {
        // A failed refetch keeps what is on screen.
        console.error('Error fetching materials:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [eventId, supabase, reloadKey]);

  // Links for a material that has just opened come from the server, so refetch
  // at the edge rather than just re-rendering.
  useTimeBoundaries(
    materials.flatMap((m) => [m.opens_at, m.closes_at]),
    () => setReloadKey((k) => k + 1),
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  const ticketLocked = materials.some((m) => m.lock_reason === 'ticket');
  const canUpgrade = ticketLocked && hasUpgradeTarget && !!onUpgradeTicket && isWithinUpgradeWindow(eventEndDate);

  const lockHint = (reason: NonNullable<MaterialLockReason>, from: string | null, until: string | null): string => {
    switch (reason) {
      case 'payment':
        return uk ? 'Стане доступним після оплати' : 'Available once your payment is confirmed';
      case 'ticket':
        return uk ? 'Не входить у ваш квиток' : 'Not included in your ticket';
      case 'upcoming':
        return from
          ? (uk ? `Доступно з ${formatShortDateTime(from, 'о')}` : `Available from ${formatShortDateTime(from, 'at')}`)
          : (uk ? 'Ще не доступно' : 'Not available yet');
      case 'closed':
        return until
          ? (uk ? `Доступ завершився ${formatShortDateTime(until, 'о')}` : `Access ended ${formatShortDateTime(until, 'at')}`)
          : (uk ? 'Доступ завершився' : 'Access has ended');
    }
  };

  return (
    <div className="space-y-4">
      {ticketLocked && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-sm text-gray-700 flex-1">
            {hasUpgradeTarget
              ? (uk ? 'Матеріали події входять в інший тип квитка.' : 'Event materials come with a different ticket type.')
              : (uk ? 'Ваш квиток не включає доступ до матеріалів.' : 'Your ticket does not include access to materials.')}
          </p>
          {canUpgrade && (
            <Button onClick={onUpgradeTicket} className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0">
              <ArrowUpCircle className="h-4 w-4 mr-2" />
              {uk ? 'Змінити квиток' : 'Upgrade ticket'}
            </Button>
          )}
        </div>
      )}

      {materials.map((material) => {
        const reason = material.lock_reason;

        if (reason) {
          return (
            <div key={material.id} className="border-2 border-gray-200 rounded-lg p-6 bg-gray-50">
              <div className="flex items-start gap-3">
                {reason === 'upcoming'
                  ? <Clock className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
                  : <Lock className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />}
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-lg text-gray-700 break-words">{material.title}</h3>
                  {material.description && (
                    <p className="text-gray-500 mt-2 text-sm">{material.description}</p>
                  )}
                  <p className="text-sm text-gray-500 mt-2">{lockHint(reason, material.opens_at, material.closes_at)}</p>
                </div>
              </div>
            </div>
          );
        }

        // YouTube / Vimeo recordings play on our own full-screen page instead of
        // sending the participant off to the video host.
        const isEmbeddableVideo = !material.file_url && !!parseVideoUrl(material.external_url);
        if (isEmbeddableVideo) {
          return (
            <Link
              key={material.id}
              href={recordingPath(eventId, material.id)}
              className="block border-2 border-gray-200 rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all bg-white cursor-pointer group"
            >
              <div className="flex items-start gap-3">
                <PlayCircle className="h-5 w-5 text-gray-500 flex-shrink-0 mt-1 group-hover:text-blue-500 transition-colors" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-lg text-gray-900 break-words group-hover:text-blue-600 transition-colors">{material.title}</h3>
                  {material.description && (
                    <p className="text-gray-600 mt-2 text-sm">{material.description}</p>
                  )}
                  <p className="text-sm text-blue-600 mt-3 font-medium">{t('recording.watch')}</p>
                </div>
              </div>
            </Link>
          );
        }

        return (
          <a
            key={material.id}
            href={material.file_url || material.external_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="block border-2 border-gray-200 rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all bg-white cursor-pointer group"
            onClick={(e) => {
              // Don't navigate if clicking on the download button
              if ((e.target as HTMLElement).closest('[data-no-navigate]')) {
                e.preventDefault();
              }
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <FileText className="h-5 w-5 text-gray-500 flex-shrink-0 mt-1 group-hover:text-blue-500 transition-colors" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg text-gray-900 break-words group-hover:text-blue-600 transition-colors">{material.title}</h3>
                    <ExternalLink className="h-5 w-5 text-gray-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {material.description && (
                    <p className="text-gray-600 mt-2 text-sm">{material.description}</p>
                  )}
                </div>
              </div>

              {/* Download button - separate action */}
              {material.file_url && material.is_downloadable && (
                <button
                  data-no-navigate
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const link = document.createElement('a');
                    link.href = asDownloadUrl(material.file_url!);
                    link.click();
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition-colors flex-shrink-0"
                  title={t('common.download') || 'Download'}
                >
                  <Download className="h-5 w-5" />
                </button>
              )}
            </div>
          </a>
        );
      })}
    </div>
  );
}
